import React, { useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useStore } from '../../hooks/useStore';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import AddImageModal from './AddImageModal';

const UPLOAD_URL = '/api/images/upload';

interface UploadResult {
  status: 'success' | 'error';
  url?: string;
  message?: string;
}

interface AddImageModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Container for AddImageModal (ADR-034).
 *
 * Owns all store access and the image upload API call:
 * - Reads `imageCategories` from the global store cache
 * - Reads the auth `token` for the upload Authorization header
 * - Reads `addImage` from the image library store
 * - Performs the XHR upload to `/api/images/upload` with progress tracking
 * - After successful upload, calls `addImage` to persist in the image library store
 *
 * The presenter receives `onUpload` as a callback and never touches stores or APIs directly.
 */
const AddImageModalContainer: React.FC<AddImageModalContainerProps> = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const imageCategories = useStore(state => state.session?.cache?.imageCategories);
  const addImage = useImageLibraryStore(state => state.addImage);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Uploads a compressed image file to the server via XHR (for progress tracking).
   * On success, persists the image metadata in the image library store.
   *
   * @param name - User-provided display name for the image
   * @param category - Selected category ID
   * @param compressedFile - The client-side compressed File object
   */
  const handleUpload = useCallback(async (
    name: string,
    category: string,
    compressedFile: File,
  ): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('image', compressedFile);

      const result = await uploadWithProgress(UPLOAD_URL, formData, token, setUploadProgress);

      if (result.status !== 'success' || !result.url) {
        throw new Error(result.message ?? 'Server upload failed after a successful request.');
      }

      await addImage(name, category, result.url, imageCategories);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [token, imageCategories, addImage]);

  return (
    <AddImageModal
      isOpen={isOpen}
      onClose={onClose}
      onUpload={handleUpload}
      imageCategories={imageCategories}
      isUploading={isUploading}
      uploadProgress={uploadProgress}
    />
  );
};

/**
 * Uploads a FormData payload via XHR to get upload progress events.
 * The standard `fetch` API does not support upload progress tracking,
 * so XHR is used here intentionally (not a violation of the api.ts wrapper rule,
 * since this is a multipart file upload with progress -- a use case the wrapper
 * does not support).
 */
function uploadWithProgress(
  url: string,
  formData: FormData,
  token: string | null,
  onProgress: (percent: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as UploadResult);
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed due to a network error.'));
    };

    xhr.send(formData);
  });
}

export default AddImageModalContainer;
